import { useEffect, useState } from 'react';
import catchupLogoHeader from '../assets/landing/catchup-logo-header.png';
import heroAssets from '../assets/landing/hero-assets.png';
import heroAssetsUpscaledWebp from '../assets/landing/hero-assets-upscaled.webp';
import heroAssetsWebp from '../assets/landing/hero-assets.webp';
import mobileHeroArtCrop from '../assets/landing/mobile-hero-art-crop.png';
import mobileHeroArtCropUpscaledWebp from '../assets/landing/mobile-hero-art-crop-upscaled.webp';
import mobileHeroArtCropWebp from '../assets/landing/mobile-hero-art-crop.webp';
import { HowItWorksSection } from './HowItWorksSection';
import { OrganizerSection } from './OrganizerSection';

const company = 'CatchUp AI';
const product = 'CatchUp AI';
const businessAddress = '8735 Dunwoody Place #10787, Atlanta, GA 30350, USA';
const contactEmail = 'mooreeric28@gmail.com';
const landingTitle = 'CatchUp - Make plans without managing the group chat';
const landingDescription = 'CatchUp helps friends find a time, confirm the plan, and keep everyone in the loop without endless scheduling messages.';
const landingUrl = 'https://catchupai.ai/';
const landingOgImage = new URL(heroAssets, landingUrl).href;

const navItems = [
  ['How it works', '#how-it-works'],
  ['For friends', '#for-friends'],
  ['Use cases', '#use-cases'],
  ['SMS policy', '#sms-consent'],
  ['FAQ', '#faq'],
] as const;

const benefits = [
  ['people', 'No app needed for guests'],
  ['sun', 'Smart time matching'],
  ['bell', 'Gentle nudges that help'],
  ['lock', 'Private and secure'],
] as const;

const trustItems = [
  ['heart', 'Built for real relationships, not just schedules.'],
  ['shield', 'Privacy by design'],
  ['users', 'Clear support controls'],
] as const;

const productOutcomes = [
  {
    icon: 'people',
    title: 'No account required',
    body: 'Guests can open one link and respond from the browser, so the plan keeps moving without another signup.',
    detail: 'Browser response',
  },
  {
    icon: 'bell',
    title: 'Transactional by design',
    body: 'Invites, reminders, confirmations, and reschedules stay tied to scheduling instead of turning into marketing.',
    detail: 'Scheduling only',
  },
  {
    icon: 'shield',
    title: 'Clear policies and support',
    body: 'CatchUp AI keeps privacy, terms, STOP, and HELP details visible so guests know what happens next.',
    detail: 'Privacy + Terms',
  },
] as const;

const useCases = [
  {
    icon: '☕',
    title: 'Coffee with one friend',
    friction: 'Simple plans still get stuck when both people answer later.',
    help: 'CatchUp gives one lightweight place to pick a time and confirm.',
  },
  {
    icon: '🍽',
    title: 'Dinner with a small group',
    friction: 'Three or four calendars can turn a dinner idea into a thread.',
    help: 'Everyone shares availability once, then the organizer can choose.',
  },
  {
    icon: '★',
    title: 'Game night',
    friction: 'Group chats bury the best options under side conversations.',
    help: 'CatchUp keeps options, responses, and the next step together.',
  },
  {
    icon: '☼',
    title: 'Weekend activity',
    friction: 'Loose plans fade when nobody knows who is actually free.',
    help: 'Availability turns a vague idea into a realistic plan window.',
  },
  {
    icon: '♥',
    title: 'Visiting family or friends',
    friction: 'People want to make it work but need a gentle way to coordinate.',
    help: 'Guests can respond from a link without downloading anything.',
  },
] as const;

const faqItems = [
  {
    question: 'Do guests need to install the app?',
    answer: 'No. Guests can respond from the invite link in their browser, so they can help pick a time without creating an account or downloading another app.',
  },
  {
    question: 'What kinds of texts does CatchUp send?',
    answer: 'CatchUp AI sends transactional scheduling messages such as invitations, availability requests, confirmations, reschedules, and reminders.',
  },
  {
    question: 'Can someone opt out?',
    answer: 'Yes. Anyone receiving SMS messages can reply STOP to opt out or HELP for help. Message frequency varies and message and data rates may apply.',
  },
  {
    question: 'Is CatchUp for work meetings?',
    answer: 'CatchUp is designed for personal plans with friends, family, and small groups rather than workplace calendar management.',
  },
  {
    question: 'Who operates CatchUp?',
    answer: 'CatchUp AI operates the public homepage, policies, invite links, and transactional scheduling message program.',
  },
  {
    question: 'Where can I read the privacy policy?',
    answer: 'The Privacy Policy and Terms of Service are linked from the header, SMS policy section, final CTA, and footer.',
  },
] as const;

const heroImageSizes = '(min-width: 1280px) 57vw, (min-width: 768px) calc(100vw - 48px), calc(100vw + 48px)';
const desktopHeroPngSrcSet = `${heroAssets} 2680w`;
const desktopHeroWebpSrcSet = `${heroAssetsWebp} 2680w, ${heroAssetsUpscaledWebp} 4096w`;
const mobileHeroPngSrcSet = `${mobileHeroArtCrop} 958w`;
const mobileHeroWebpSrcSet = `${mobileHeroArtCropWebp} 958w, ${mobileHeroArtCropUpscaledWebp} 1916w`;

function upsertMeta(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector(selector) as HTMLMetaElement | HTMLLinkElement | null;
  if (!element) {
    element = document.createElement(attributes.rel ? 'link' : 'meta') as HTMLMetaElement | HTMLLinkElement;
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([name, value]) => {
    element?.setAttribute(name, value);
  });
}

function HomeMeta() {
  useEffect(() => {
    document.title = landingTitle;
    upsertMeta('meta[name="description"]', { name: 'description', content: landingDescription });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: landingTitle });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: landingDescription });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: landingOgImage });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: landingUrl });
    upsertMeta('link[rel="canonical"]', { rel: 'canonical', href: landingUrl });

    const existingSchema = document.head.querySelector('script[data-catchup-landing-schema="true"]');
    existingSchema?.remove();
    const schema = document.createElement('script');
    schema.type = 'application/ld+json';
    schema.dataset.catchupLandingSchema = 'true';
    schema.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          name: company,
          url: landingUrl,
          email: contactEmail,
          address: businessAddress,
        },
        {
          '@type': 'WebApplication',
          name: 'CatchUp',
          applicationCategory: 'LifestyleApplication',
          operatingSystem: 'Web, iOS',
          url: landingUrl,
          description: landingDescription,
        },
        {
          '@type': 'FAQPage',
          mainEntity: faqItems.map(({ question, answer }) => ({
            '@type': 'Question',
            name: question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: answer,
            },
          })),
        },
      ],
    });
    document.head.appendChild(schema);
  }, []);

  return null;
}

function Mark({ type }: { type: string }) {
  const marks: Record<string, string> = {
    bell: '!',
    heart: '♥',
    lock: '□',
    people: '◎',
    shield: '◇',
    sun: '☼',
    users: '♧',
  };

  return (
    <span
      aria-hidden="true"
      className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#F1EDFF] text-2xl font-bold text-[#3926E8]"
    >
      {marks[type]}
    </span>
  );
}

function ProblemSection() {
  const [responsePreviewStatus, setResponsePreviewStatus] = useState('');

  return (
    <section
      aria-labelledby="problem-title"
      className="relative overflow-hidden bg-white px-6 pb-11 pt-8 text-center md:px-8 lg:px-10"
    >
      <div className="pointer-events-none absolute left-[8%] top-[3%] h-56 w-56 rounded-full bg-[#7F65FF]/[0.06] blur-3xl" />
      <div className="pointer-events-none absolute right-[8%] top-[10%] h-64 w-64 rounded-full bg-[#F6D3C5]/20 blur-3xl" />

      <div className="mx-auto max-w-[1040px]">
        <p className="inline-flex h-[39px] items-center justify-center gap-2.5 rounded-full bg-[#EDE8FF]/90 px-[18px] text-[15px] font-bold leading-none text-[#3926E8]">
          <svg aria-hidden="true" className="h-[17px] w-[17px] text-[#B292FF]" fill="none" viewBox="0 0 24 24">
            <path
              d="M7 8.5h10M7 12h6m6.5 5.5h-8.2L6.5 21v-3.5H5A2.5 2.5 0 0 1 2.5 15V7A2.5 2.5 0 0 1 5 4.5h14A2.5 2.5 0 0 1 21.5 7v8a2.5 2.5 0 0 1-2 2.5Z"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.9"
            />
          </svg>
          The problem we’re here to solve
        </p>
        <h2
          className="mx-auto mt-[27px] max-w-[1000px] font-serif text-[42px] font-black leading-[0.95] tracking-normal text-[#080D3D] sm:text-[60px] lg:text-[76px]"
          id="problem-title"
        >
          Plans should
          <br className="hidden sm:block" />
          {' '}not die in the <span className="text-[#F5665F]">group chat.</span>
        </h2>
        <p className="mx-auto mt-6 max-w-[620px] text-lg font-medium leading-[1.45] text-[#404B75] sm:text-[21px]">
          We’ve all been there. Good intentions, messy threads,
          <br className="hidden sm:block" />
          {' '}and plans that never happen.
        </p>
      </div>

      <div className="relative mx-auto mt-12 w-full max-w-[1268px]">
        <div aria-hidden="true" className="absolute -left-[43px] bottom-[69px] hidden h-[170px] w-[66px] rotate-[-8deg] opacity-70 xl:block">
          <span className="absolute bottom-0 left-[11px] block h-[50px] w-4 origin-bottom rotate-[-61deg] rounded-[999px_0_999px_0] bg-gradient-to-b from-[#A587FF]/80 to-[#D7CAFF]/40" />
          <span className="absolute bottom-[43px] left-2.5 block h-[50px] w-4 origin-bottom rotate-[-58deg] rounded-[999px_0_999px_0] bg-gradient-to-b from-[#A587FF]/80 to-[#D7CAFF]/40" />
          <span className="absolute bottom-[78px] left-[29px] block h-[50px] w-4 origin-bottom rotate-[38deg] rounded-[999px_0_999px_0] bg-gradient-to-b from-[#A587FF]/80 to-[#D7CAFF]/40" />
          <span className="absolute bottom-[118px] left-[30px] block h-[50px] w-4 origin-bottom rotate-[20deg] rounded-[999px_0_999px_0] bg-gradient-to-b from-[#A587FF]/80 to-[#D7CAFF]/40" />
          <span className="absolute bottom-0 left-[29px] block h-[152px] w-0.5 rotate-[-6deg] rounded-full bg-[#A281FF]/50" />
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <article className="relative grid min-h-[520px] grid-rows-[76px_62px_82px_1fr] overflow-hidden rounded-[20px] bg-[radial-gradient(circle_at_50%_1%,rgba(255,255,255,0.9),transparent_38%),linear-gradient(180deg,rgba(249,248,255,0.96)_0%,rgba(250,249,255,0.86)_100%)] px-4 text-center text-[#080D3D] shadow-[0_26px_70px_rgba(44,32,112,0.08)] transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_30px_78px_rgba(44,32,112,0.12)] motion-reduce:transform-none motion-reduce:transition-none sm:px-6">
            <span aria-hidden="true" className="relative grid h-[69px] w-[69px] place-items-center self-end justify-self-center rounded-full bg-white/80 shadow-[inset_0_0_0_10px_rgba(255,255,255,0.52)]">
              <span className="absolute h-[45px] w-[45px] rounded-full bg-[#EEE9FF]" />
              <svg className="relative z-10 h-[30px] w-[30px] text-[#4B2CFF]" fill="none" viewBox="0 0 32 32">
                <path d="M8.5 9.5h15A3.5 3.5 0 0 1 27 13v6.5a3.5 3.5 0 0 1-3.5 3.5h-8.1l-5.1 3.6V23H8.5A3.5 3.5 0 0 1 5 19.5V13a3.5 3.5 0 0 1 3.5-3.5Z" stroke="currentColor" strokeLinejoin="round" strokeWidth="2" />
                <path d="M11.7 16.4h.1m4.1 0h.1m4.1 0h.1" stroke="currentColor" strokeLinecap="round" strokeWidth="3.2" />
              </svg>
            </span>
            <h3 className="mx-auto self-end px-2 text-[22px] font-black leading-[1.15] tracking-normal text-[#080D3D] lg:text-2xl">
              The endless back-and-forth
            </h3>
            <p className="mx-auto mt-3 px-4 text-[17px] font-medium leading-[1.35] text-[#404B75] lg:text-[19px]">
              “Does Tuesday work? What
              <br />
              about Friday? Who’s free?”
            </p>
            <div aria-hidden="true" className="relative mx-auto h-[220px] w-[336px] max-w-full">
              <span className="absolute left-[22px] top-6 grid h-[39px] w-[39px] place-items-center rounded-full border-[3px] border-white bg-gradient-to-br from-[#4DB6E8] to-[#2760C9] text-xs font-black text-white shadow-[0_4px_12px_rgba(13,17,58,0.14)]">A</span>
              <span className="absolute left-[68px] top-[22px] flex min-h-[39px] w-[222px] max-w-[66%] items-center rounded-[13px] bg-[#E9E4FF] px-[22px] text-left text-base font-semibold leading-none text-[#080D3D] shadow-[0_8px_18px_rgba(53,48,93,0.06)]">Does Tuesday work?</span>
              <span className="absolute left-[70px] top-[72px] flex min-h-[39px] w-[262px] max-w-[78%] items-center rounded-[13px] bg-white px-[22px] text-left text-base font-semibold leading-none text-[#080D3D] shadow-[0_8px_18px_rgba(53,48,93,0.06)]">What about Friday?</span>
              <span className="absolute right-[18px] top-[75px] grid h-[39px] w-[39px] place-items-center rounded-full border-[3px] border-white bg-gradient-to-br from-[#F5A16F] to-[#2B1D2B] text-xs font-black text-white shadow-[0_4px_12px_rgba(13,17,58,0.14)]">M</span>
              <span className="absolute left-[23px] top-[124px] grid h-[39px] w-[39px] place-items-center rounded-full border-[3px] border-white bg-gradient-to-br from-[#55C3EF] to-[#2D6D9A] text-xs font-black text-white shadow-[0_4px_12px_rgba(13,17,58,0.14)]">J</span>
              <span className="absolute left-[68px] top-[122px] flex min-h-[39px] w-[242px] max-w-[72%] items-center rounded-[13px] bg-[#E9E4FF] px-[22px] text-left text-base font-semibold leading-none text-[#080D3D] shadow-[0_8px_18px_rgba(53,48,93,0.06)]">I might be free Saturday?</span>
              <span className="absolute left-[70px] top-[172px] flex min-h-[39px] w-[242px] max-w-[72%] items-center rounded-[13px] bg-white px-[22px] text-left text-base font-semibold leading-none text-[#080D3D] shadow-[0_8px_18px_rgba(53,48,93,0.06)]">Anyone else?</span>
              <span className="absolute right-[18px] top-[170px] grid h-[39px] w-[39px] place-items-center rounded-full border-[3px] border-white bg-gradient-to-br from-[#F0A46D] to-[#20293F] text-xs font-black text-white shadow-[0_4px_12px_rgba(13,17,58,0.14)]">S</span>
              <span className="absolute left-[27px] top-[225px] hidden h-[38px] w-[88px] items-center justify-center gap-[9px] rounded-[13px] bg-[#E9E4FF] sm:flex">
                <span className="h-2 w-2 rounded-full bg-[#A99AFA]" />
                <span className="h-2 w-2 rounded-full bg-[#A99AFA]" />
                <span className="h-2 w-2 rounded-full bg-[#A99AFA]" />
              </span>
            </div>
          </article>

          <article className="relative grid min-h-[520px] grid-rows-[76px_62px_82px_1fr] overflow-hidden rounded-[20px] bg-[radial-gradient(circle_at_50%_0%,rgba(255,255,255,0.85),transparent_37%),linear-gradient(180deg,rgba(255,246,240,0.94)_0%,rgba(255,241,232,0.84)_100%)] px-4 text-center text-[#080D3D] shadow-[0_26px_70px_rgba(44,32,112,0.08)] transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_30px_78px_rgba(44,32,112,0.12)] motion-reduce:transform-none motion-reduce:transition-none sm:px-6">
            <span aria-hidden="true" className="relative grid h-[69px] w-[69px] place-items-center self-end justify-self-center rounded-full bg-white/80 shadow-[inset_0_0_0_10px_rgba(255,255,255,0.52)]">
              <span className="absolute h-[45px] w-[45px] rounded-full bg-[#FFEFE4]" />
              <svg className="relative z-10 h-[30px] w-[30px] text-[#FF7631]" fill="none" viewBox="0 0 32 32">
                <path d="M16 7.5a10 10 0 1 0 0 20 10 10 0 0 0 0-20Z" stroke="currentColor" strokeWidth="2" />
                <path d="M16 12v5.5l3.5 2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                <path d="M13 4.5h6M16 4.5v3" stroke="currentColor" strokeLinecap="round" strokeWidth="2" />
              </svg>
            </span>
            <h3 className="mx-auto self-end px-2 text-[22px] font-black leading-[1.15] tracking-normal text-[#080D3D] lg:text-2xl">
              People mean to reconnect
            </h3>
            <p className="mx-auto mt-3 px-4 text-[17px] font-medium leading-[1.35] text-[#404B75] lg:text-[19px]">
              “But life gets busy, and weeks
              <br />
              quietly turn into months.”
            </p>
            <div aria-hidden="true" className="relative mx-auto h-[264px] w-[318px] max-w-full overflow-hidden rounded-[20px] bg-white/85 shadow-[0_18px_38px_rgba(135,88,59,0.08)]">
              <div className="grid grid-cols-7 px-[22px] pb-2 pt-[22px] text-sm font-medium text-[#6C748D]">
                <span>S</span><span>M</span><span>T</span><span>W</span><span>T</span><span>F</span><span>S</span>
              </div>
              <div className="grid grid-cols-7 gap-x-[26px] gap-y-[18px] px-7 pt-3">
                <span className="h-[13px] w-[13px] rounded-full bg-[#F7EEE9]" /><span className="h-[13px] w-[13px] rounded-full bg-[#E6E7ED]" /><span className="h-[13px] w-[13px] rounded-full bg-[#E6E7ED]" /><span className="h-[13px] w-[13px] rounded-full bg-[#E6E7ED]" /><span className="h-[13px] w-[13px] rounded-full bg-[#E6E7ED]" /><span className="h-[13px] w-[13px] rounded-full bg-[#E6E7ED]" /><span className="h-[13px] w-[13px] rounded-full bg-[#E6E7ED]" />
                <span className="h-[13px] w-[13px] rounded-full bg-[#F7EEE9]" /><span className="h-[13px] w-[13px] rounded-full bg-[#E6E7ED]" /><span className="h-[13px] w-[13px] rounded-full bg-[#E6E7ED]" /><span className="h-[13px] w-[13px] rounded-full bg-[#E6E7ED]" /><span className="h-[13px] w-[13px] rounded-full bg-[#E6E7ED]" /><span className="h-[13px] w-[13px] rounded-full bg-[#E6E7ED]" /><span className="h-[13px] w-[13px] rounded-full bg-[#E6E7ED]" />
                <span className="-ml-1 -mt-[5px] h-[22px] w-[22px] rounded-full bg-[#FF7631]" /><span className="h-[13px] w-[13px] rounded-full bg-[#F7EEE9]" /><span className="h-[13px] w-[13px] rounded-full bg-[#E6E7ED]" /><span className="h-[13px] w-[13px] rounded-full bg-[#E6E7ED]" /><span className="h-[13px] w-[13px] rounded-full bg-[#E6E7ED]" /><span className="h-[13px] w-[13px] rounded-full bg-[#E6E7ED]" /><span className="-ml-[5px] -mt-1.5 h-6 w-6 rounded-full border-[3px] border-[#FF7631]" />
                <span className="h-[13px] w-[13px] rounded-full bg-[#F7EEE9]" /><span className="h-[13px] w-[13px] rounded-full bg-[#F7EEE9]" /><span className="h-[13px] w-[13px] rounded-full bg-[#E6E7ED]" /><span className="h-[13px] w-[13px] rounded-full bg-[#E6E7ED]" /><span className="h-[13px] w-[13px] rounded-full bg-[#F7EEE9]" /><span className="h-[13px] w-[13px] rounded-full bg-[#F7EEE9]" /><span className="h-[13px] w-[13px] rounded-full bg-[#F7EEE9]" />
              </div>
              <svg className="absolute left-[42px] top-[127px] h-[60px] w-60" fill="none" viewBox="0 0 240 60">
                <path d="M12 10C72 62 151 62 227 11" stroke="#FF7631" strokeDasharray="5 8" strokeLinecap="round" strokeWidth="2" />
              </svg>
              <span className="absolute bottom-[25px] left-0 right-0 rotate-[-1deg] font-['Bradley_Hand','Comic_Sans_MS',cursive] text-xl font-bold tracking-[0.2px] text-[#FF7631]">Weeks turn into months</span>
              <span className="absolute bottom-[19px] right-[78px] h-3 w-[70px] rotate-[-4deg] rounded-[50%] border-b-2 border-[#FF7631]" />
            </div>
          </article>

          <article className="relative grid min-h-[520px] grid-rows-[76px_62px_82px_1fr] overflow-hidden rounded-[20px] bg-[radial-gradient(circle_at_50%_1%,rgba(255,255,255,0.9),transparent_38%),linear-gradient(180deg,rgba(249,248,255,0.96)_0%,rgba(250,249,255,0.86)_100%)] px-4 text-center text-[#080D3D] shadow-[0_26px_70px_rgba(44,32,112,0.08)] transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_30px_78px_rgba(44,32,112,0.12)] motion-reduce:transform-none motion-reduce:transition-none sm:px-6">
            <span aria-hidden="true" className="relative grid h-[69px] w-[69px] place-items-center self-end justify-self-center rounded-full bg-white/80 shadow-[inset_0_0_0_10px_rgba(255,255,255,0.52)]">
              <span className="absolute h-[45px] w-[45px] rounded-full bg-[#EEE9FF]" />
              <svg className="relative z-10 h-[30px] w-[30px] text-[#4B2CFF]" fill="none" viewBox="0 0 32 32">
                <path d="M13.5 18.5 18.7 13a4.1 4.1 0 1 1 5.8 5.8l-6.1 6.1a4.1 4.1 0 0 1-5.8 0 4 4 0 0 1-1-4" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" />
                <path d="M18.6 13.5 13.4 19a4.1 4.1 0 1 1-5.8-5.8l6.1-6.1a4.1 4.1 0 0 1 5.8 0 4 4 0 0 1 1 4" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" />
              </svg>
            </span>
            <h3 className="mx-auto self-end px-1 text-[22px] font-black leading-[1.15] tracking-normal text-[#080D3D] lg:text-2xl xl:whitespace-nowrap xl:text-[21px]">
              Guests do not want another app
            </h3>
            <p className="mx-auto mt-3 px-4 text-[17px] font-medium leading-[1.35] text-[#404B75] lg:text-[19px]">
              “They just want to tap a link
              <br />
              and respond.”
            </p>
            <div className="relative mx-auto h-[318px] w-[232px] max-w-full overflow-hidden rounded-t-[31px] border-[7px] border-b-0 border-[#E5E5EC] bg-white shadow-[0_12px_26px_rgba(45,42,90,0.06)]">
              <span aria-hidden="true" className="absolute -top-px left-1/2 h-[18px] w-[105px] -translate-x-1/2 rounded-b-[11px] bg-[#E5E5EC]" />
              <span aria-hidden="true" className="mx-auto mt-[42px] grid h-[84px] w-[84px] place-items-center rounded-full bg-[#EEE9FF]">
                <svg className="h-[42px] w-[42px] text-[#4B2CFF]" fill="none" viewBox="0 0 48 48">
                  <path d="M9 14h30v22H9V14Z" stroke="currentColor" strokeLinejoin="round" strokeWidth="3" />
                  <path d="m10 15 14 13 14-13" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" />
                </svg>
              </span>
              <h4 className="mt-[22px] text-xl font-black leading-tight text-[#080D3D]">You’re invited!</h4>
              <p className="mx-auto mt-2 max-w-[135px] text-base leading-[1.35] text-[#404B75]">Tap to choose what works for you.</p>
              <button
                className="mt-3 inline-flex h-11 w-[157px] items-center justify-center rounded-[11px] bg-gradient-to-b from-[#4A2CFF] to-[#341CFF] text-base font-bold text-white shadow-[0_13px_24px_rgba(57,38,232,0.22)] focus:outline-none focus:ring-4 focus:ring-[#3926E8]/30"
                onClick={() => setResponsePreviewStatus('Preview response saved. Guests can respond from one link.')}
                type="button"
              >
                Respond
              </button>
              {responsePreviewStatus && (
                <p className="mx-auto mt-2 max-w-[168px] px-2 text-[13px] font-bold leading-tight text-[#3926E8]" role="status">
                  {responsePreviewStatus}
                </p>
              )}
            </div>
          </article>
        </div>

        <div aria-hidden="true" className="absolute -right-[43px] bottom-[69px] hidden h-[170px] w-[66px] scale-x-[-1] rotate-[-8deg] opacity-70 xl:block">
          <span className="absolute bottom-0 left-[11px] block h-[50px] w-4 origin-bottom rotate-[-61deg] rounded-[999px_0_999px_0] bg-gradient-to-b from-[#A587FF]/80 to-[#D7CAFF]/40" />
          <span className="absolute bottom-[43px] left-2.5 block h-[50px] w-4 origin-bottom rotate-[-58deg] rounded-[999px_0_999px_0] bg-gradient-to-b from-[#A587FF]/80 to-[#D7CAFF]/40" />
          <span className="absolute bottom-[78px] left-[29px] block h-[50px] w-4 origin-bottom rotate-[38deg] rounded-[999px_0_999px_0] bg-gradient-to-b from-[#A587FF]/80 to-[#D7CAFF]/40" />
          <span className="absolute bottom-[118px] left-[30px] block h-[50px] w-4 origin-bottom rotate-[20deg] rounded-[999px_0_999px_0] bg-gradient-to-b from-[#A587FF]/80 to-[#D7CAFF]/40" />
          <span className="absolute bottom-0 left-[29px] block h-[152px] w-0.5 rotate-[-6deg] rounded-full bg-[#A281FF]/50" />
        </div>
      </div>
    </section>
  );
}

function PublicFooter() {
  return (
    <footer className="border-t border-slate-200 bg-white">
      <div className="mx-auto flex max-w-6xl flex-col gap-5 px-5 py-8 text-sm text-slate-600 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="font-semibold text-slate-950">{company}</p>
          <p>{businessAddress}</p>
        </div>
        <nav aria-label="Legal links" className="flex flex-wrap gap-4">
          <a className="inline-flex min-h-11 items-center font-medium text-[#2C7B8B] underline underline-offset-4" href="/privacy">
            Privacy Policy
          </a>
          <a className="inline-flex min-h-11 items-center font-medium text-[#2C7B8B] underline underline-offset-4" href="/terms">
            Terms of Service
          </a>
        </nav>
      </div>
    </footer>
  );
}

function UseCasesSection() {
  return (
    <section
      aria-labelledby="use-cases-title"
      className="bg-[#FBFCFF] px-6 py-16 text-[#080D3D] md:px-8 md:py-20"
      id="use-cases"
    >
      <div className="mx-auto max-w-[1240px]">
        <div className="mx-auto max-w-[780px] text-center">
          <p className="inline-flex min-h-10 items-center justify-center gap-2 rounded-full bg-[#EDE8FF] px-5 text-[16px] font-black text-[#3926E8]">
            <span aria-hidden="true" className="text-[#FF6B5F]">♥</span>
            Use cases
          </p>
          <h2
            className="mt-5 font-serif text-[44px] font-black leading-[0.98] tracking-normal text-[#080D3D] sm:text-[60px] lg:text-[72px]"
            id="use-cases-title"
          >
            Made for plans that keep <span className="text-[#F5665F]">relationships warm</span>
          </h2>
          <p className="mx-auto mt-5 max-w-[650px] text-lg font-medium leading-[1.45] text-[#404B75] sm:text-[20px]">
            CatchUp is for the real-life plans that deserve less coordination overhead and more follow-through.
          </p>
        </div>

        <div className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          {useCases.map(({ icon, title, friction, help }) => (
            <article
              className="rounded-[18px] border border-[#E7E2F6] bg-white p-5 shadow-[0_18px_48px_rgba(44,32,112,0.07)]"
              key={title}
            >
              <span aria-hidden="true" className="grid h-12 w-12 place-items-center rounded-full bg-[#F1EDFF] text-xl font-black text-[#3926E8]">
                {icon}
              </span>
              <h3 className="mt-5 text-[20px] font-black leading-tight text-[#080D3D]">{title}</h3>
              <p className="mt-3 text-[15px] font-semibold leading-[1.45] text-[#404B75]">{friction}</p>
              <p className="mt-4 rounded-[14px] bg-[#FFF8F4] p-4 text-[14px] font-bold leading-[1.4] text-[#4D5577]">
                {help}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function SmsTrustSection() {
  const [status, setStatus] = useState('SMS proof form ready');

  return (
    <section
      aria-labelledby="sms-trust-title"
      className="bg-[#FDFBF7] px-6 py-16 text-[#080D3D] md:px-8 md:py-20"
      id="sms-consent"
    >
      <div className="mx-auto grid max-w-[1160px] gap-8 lg:grid-cols-[0.92fr_1.08fr]">
        <div>
          <p className="inline-flex min-h-10 items-center justify-center rounded-full bg-white px-5 text-[15px] font-black text-[#2C7B8B] ring-1 ring-[#DDE2EE]">
            SMS policy
          </p>
          <h2
            className="mt-5 max-w-[640px] font-serif text-[42px] font-black leading-[1] tracking-normal text-[#080D3D] sm:text-[58px]"
            id="sms-trust-title"
          >
            Join early access without the fine-print hunt
          </h2>
          <p className="mt-5 max-w-[620px] text-lg font-medium leading-[1.55] text-[#404B75]">
            Add your number for launch updates and early access. CatchUp AI will use transactional scheduling messages so guests understand why they are receiving texts and how to control them.
          </p>
          <div className="mt-7 grid gap-3 text-[15px] font-bold leading-snug text-[#404B75] sm:grid-cols-2">
            {[
              'Reply STOP to opt out',
              'Reply HELP for help',
              'Message frequency varies',
              'Message and data rates may apply',
            ].map((item) => (
              <div className="flex min-h-12 items-center gap-3 rounded-[14px] bg-white px-4 ring-1 ring-[#DDE2EE]" key={item}>
                <span aria-hidden="true" className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#F1EDFF] text-[#3926E8]">
                  ✓
                </span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        <form className="rounded-[18px] border border-[#DDE2EE] bg-white p-5 shadow-[0_24px_64px_rgba(44,32,112,0.08)] md:p-6">
          <label className="block text-sm font-bold text-[#080D3D]">
            Mobile phone number
            <input
              aria-label="Mobile phone number"
              className="mt-2 block min-h-12 w-full rounded-[12px] border border-[#C9CFDF] bg-white px-3 text-base text-[#080D3D] focus:outline-none focus:ring-4 focus:ring-[#3926E8]/20"
              name="phone"
              placeholder="(555) 555-5555"
              type="tel"
            />
          </label>
          <label className="mt-5 flex items-start gap-3 text-sm font-medium leading-6 text-[#404B75]">
            <input
              aria-label="receive transactional SMS"
              className="mt-1 h-5 w-5 rounded border-[#C9CFDF]"
              name="smsConsent"
              type="checkbox"
            />
            <span>
              I agree to receive transactional SMS messages from {product} about scheduling links, availability
              requests, confirmations, reschedules, and reminders. Message
              frequency varies. Message and data rates may apply. Reply STOP
              to unsubscribe or HELP for help. I agree to the{' '}
              <a className="inline-flex min-h-11 items-center align-middle font-bold text-[#2C7B8B] underline underline-offset-4" href="/privacy">
                Privacy Policy
              </a>{' '}
              and{' '}
              <a className="inline-flex min-h-11 items-center align-middle font-bold text-[#2C7B8B] underline underline-offset-4" href="/terms">
                Terms of Service
              </a>
              .
            </span>
          </label>
          <button
            className="mt-5 min-h-12 rounded-[12px] bg-[#2C7B8B] px-5 font-bold text-white shadow-[0_14px_28px_rgba(44,123,139,0.16)] focus:outline-none focus:ring-4 focus:ring-[#2C7B8B]/20"
            onClick={() => setStatus('Proof form only. A real waitlist backend is not connected yet.')}
            type="button"
          >
            Request early access
          </button>
          <p className="mt-4 text-sm font-medium text-[#56607F]" role="status">
            {status}
          </p>
        </form>
      </div>
    </section>
  );
}

function FAQSection() {
  const [openQuestion, setOpenQuestion] = useState<string | null>(null);

  return (
    <section
      aria-labelledby="faq-title"
      className="bg-white px-6 py-16 text-[#080D3D] md:px-8 md:py-20"
      id="faq"
    >
      <div className="mx-auto max-w-[920px]">
        <div className="text-center">
          <p className="inline-flex min-h-10 items-center justify-center rounded-full bg-[#EDE8FF] px-5 text-[16px] font-black text-[#3926E8]">
            FAQ
          </p>
          <h2
            className="mt-5 font-serif text-[44px] font-black leading-[0.98] tracking-normal text-[#080D3D] sm:text-[60px]"
            id="faq-title"
          >
            Questions people ask before they tap
          </h2>
        </div>
        <div className="mt-9 overflow-hidden rounded-[18px] border border-[#E1E5F2]">
          {faqItems.map(({ question, answer }, index) => {
            const open = openQuestion === question;
            return (
              <div className={index > 0 ? 'border-t border-[#E1E5F2]' : ''} key={question}>
                <button
                  aria-expanded={open}
                  className="flex min-h-[68px] w-full items-center justify-between gap-5 bg-white px-5 py-4 text-left text-[17px] font-black text-[#080D3D] hover:bg-[#FBFCFF] focus:outline-none focus:ring-4 focus:ring-inset focus:ring-[#3926E8]/20"
                  onClick={() => setOpenQuestion(open ? null : question)}
                  type="button"
                >
                  <span>{question}</span>
                  <span aria-hidden="true" className="text-2xl text-[#3926E8]">{open ? '−' : '+'}</span>
                </button>
                {open && (
                  <p className="px-5 pb-5 text-base font-medium leading-[1.55] text-[#404B75]">
                    {answer}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FinalCTASection() {
  return (
    <section
      aria-labelledby="final-cta-title"
      className="bg-[#FBFCFF] px-6 py-16 text-center text-[#080D3D] md:px-8 md:py-20"
      id="final-cta"
    >
      <div className="mx-auto max-w-[760px]">
        <h2
          className="font-serif text-[44px] font-black leading-[0.98] tracking-normal text-[#080D3D] sm:text-[60px]"
          id="final-cta-title"
        >
          Be first when CatchUp is ready
        </h2>
        <p className="mx-auto mt-5 max-w-[560px] text-lg font-medium leading-[1.45] text-[#404B75] sm:text-[20px]">
          Join the early access list and we will let you know when CatchUp is ready for your first plan.
        </p>
        <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <a
            className="inline-flex min-h-16 items-center justify-center rounded-xl bg-[#3926E8] px-8 text-lg font-bold text-white shadow-[0_18px_35px_rgba(57,38,232,0.22)] hover:bg-[#2F20C8] focus:outline-none focus:ring-4 focus:ring-[#3926E8]/20"
            href="#sms-consent"
          >
            Join the early access list
          </a>
        </div>
        <nav aria-label="Final legal links" className="mt-6 flex flex-wrap justify-center gap-5 text-sm font-bold text-[#404B75]">
          <a className="inline-flex min-h-11 items-center underline-offset-4 hover:text-[#3926E8] hover:underline" href="/privacy">
            Privacy Policy
          </a>
          <a className="inline-flex min-h-11 items-center underline-offset-4 hover:text-[#3926E8] hover:underline" href="/terms">
            Terms of Service
          </a>
          <a className="inline-flex min-h-11 items-center underline-offset-4 hover:text-[#3926E8] hover:underline" href="#sms-consent">
            SMS policy
          </a>
        </nav>
      </div>
    </section>
  );
}

export function HomePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#FBFCFF] text-[#050B2F]">
      <HomeMeta />
      <header className="relative z-20 bg-white/90">
        <div className="mx-auto flex max-w-[1480px] flex-wrap items-center justify-between gap-5 px-6 py-6 md:flex-nowrap md:px-8">
          <a className="block shrink-0 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#3926E8]/20" href="/">
            <img
              alt="CatchUp"
              className="h-[45px] w-[185px] object-contain md:h-[54px] md:w-[222px]"
              src={catchupLogoHeader}
            />
          </a>
          <button
            aria-expanded={mobileMenuOpen}
            aria-label="Open navigation menu"
            className="grid h-11 w-11 place-items-center rounded-xl text-4xl font-bold leading-none text-[#071039] focus:outline-none focus:ring-4 focus:ring-[#3926E8]/20 md:hidden"
            onClick={() => setMobileMenuOpen((open) => !open)}
            type="button"
          >
            ≡
          </button>
          <nav aria-label="Primary" className="order-3 hidden w-full flex-wrap items-center gap-x-7 gap-y-3 text-base font-semibold text-[#071039] md:order-none md:flex md:w-auto md:justify-center">
            {navItems.map(([label, href]) => (
              <a className="inline-flex min-h-11 items-center rounded-lg focus:outline-none focus:ring-4 focus:ring-[#3926E8]/20 hover:text-[#3926E8]" href={href} key={href}>
                {label}
              </a>
            ))}
          </nav>
          <div className="hidden items-center text-base font-semibold text-[#071039] md:flex">
            <a className="rounded-xl bg-[#3926E8] px-7 py-4 text-white shadow-[0_12px_28px_rgba(57,38,232,0.25)] hover:bg-[#2F20C8] focus:outline-none focus:ring-4 focus:ring-[#3926E8]/20" href="#sms-consent">
              Early access
            </a>
          </div>
        </div>
        {mobileMenuOpen && (
          <nav aria-label="Mobile primary" className="border-t border-[#E1E5F2] bg-white px-6 py-4 md:hidden">
            <div className="grid gap-2 text-base font-bold text-[#071039]">
              {navItems.map(([label, href]) => (
                <a
                  className="inline-flex min-h-12 items-center rounded-[12px] px-3 hover:bg-[#F1EDFF] hover:text-[#3926E8] focus:outline-none focus:ring-4 focus:ring-[#3926E8]/20"
                  href={href}
                  key={href}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {label}
                </a>
              ))}
              <a
                className="mt-2 inline-flex min-h-12 items-center justify-center rounded-xl bg-[#3926E8] px-6 font-bold text-white focus:outline-none focus:ring-4 focus:ring-[#3926E8]/20"
                href="#sms-consent"
                onClick={() => setMobileMenuOpen(false)}
              >
                Early access
              </a>
            </div>
          </nav>
        )}
      </header>

      <main>
        <section className="mx-auto grid max-w-[1480px] gap-8 overflow-hidden px-6 pb-8 pt-8 md:px-8 xl:min-h-[845px] xl:grid-cols-[0.43fr_0.57fr] xl:items-center xl:pb-0 xl:pt-0">
          <div className="relative z-10 max-w-[650px]">
            <p className="mb-7 inline-flex items-center gap-3 rounded-full bg-[#F1EDFF] px-4 py-3 text-base font-medium text-[#3926E8] md:text-base">
              <span aria-hidden="true" className="text-2xl text-[#FF6B5F]">☼</span>
              Coming soon: calm scheduling for real relationships.
            </p>
            <h1 className="max-w-[630px] font-serif text-[40px] font-black leading-[1.04] tracking-normal text-[#071039] sm:text-[64px] md:text-[68px] lg:text-[72px]">
              Stay <span className="text-[#F36D64]">close</span> without the scheduling back-and-forth.
            </h1>
            <p className="mt-8 max-w-[560px] text-[22px] leading-[1.45] text-[#071039]">
              CatchUp is being built to handle the planning, so you can focus on what matters--showing up for the people who matter.
            </p>
            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <a
                className="inline-flex min-h-16 items-center justify-center rounded-xl bg-[#3926E8] px-8 text-lg font-bold text-white shadow-[0_18px_35px_rgba(57,38,232,0.22)] hover:bg-[#2F20C8] focus:outline-none focus:ring-4 focus:ring-[#3926E8]/20"
                href="#sms-consent"
              >
                Join the early access list
              </a>
              <a
                className="inline-flex min-h-16 items-center justify-center gap-3 rounded-xl border border-[#C9CFDF] bg-white px-8 text-lg font-bold text-[#071039] shadow-sm hover:border-[#3926E8] focus:outline-none focus:ring-4 focus:ring-[#3926E8]/20"
                href="#how-it-works"
              >
                <span aria-hidden="true" className="grid h-7 w-7 place-items-center rounded-full border-2 border-[#071039] text-sm">▶</span>
                See how it works
              </a>
            </div>

            <div
              aria-hidden="true"
              className="mt-8 max-h-[238px] overflow-hidden rounded-[28px] bg-[#F8F6FF] shadow-[0_20px_60px_rgba(17,24,68,0.08)] md:hidden"
              data-testid="mobile-hero-preview"
            >
              <picture className="block">
                <source media="(max-width: 767px)" srcSet={mobileHeroWebpSrcSet} type="image/webp" />
                <source media="(max-width: 767px)" srcSet={mobileHeroPngSrcSet} type="image/png" />
                <img
                  alt=""
                  className="-mx-6 -mt-3 w-[calc(100%+48px)] max-w-none object-contain"
                  loading="eager"
                  src={mobileHeroArtCrop}
                />
              </picture>
            </div>

            <div className="mt-9 grid grid-cols-4 rounded-2xl bg-white shadow-[0_24px_70px_rgba(17,24,68,0.10)]">
              {benefits.map(([icon, label], index) => (
                <div
                  className={`grid min-h-[108px] grid-rows-[44px_auto] place-items-center gap-2 px-2 py-4 text-center text-[13px] font-semibold leading-snug text-[#071039] sm:min-h-[118px] sm:gap-3 sm:px-5 sm:py-5 sm:text-base ${index > 0 ? 'border-l border-[#DDE2EE]' : ''}`}
                  key={label}
                >
                  <Mark type={icon} />
                  <span className="self-start">{label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="relative hidden overflow-hidden md:block xl:min-h-[845px] xl:overflow-visible">
            <picture className="block">
              <source media="(min-width: 1280px)" srcSet={desktopHeroWebpSrcSet} type="image/webp" />
              <source media="(min-width: 1280px)" srcSet={desktopHeroPngSrcSet} type="image/png" />
              <source media="(min-width: 768px)" srcSet={desktopHeroWebpSrcSet} type="image/webp" />
              <source media="(min-width: 768px)" srcSet={desktopHeroPngSrcSet} type="image/png" />
              <source media="(max-width: 767px)" srcSet={mobileHeroWebpSrcSet} type="image/webp" />
              <source media="(max-width: 767px)" srcSet={mobileHeroPngSrcSet} type="image/png" />
              <img
                alt="CatchUp scheduling app preview"
                className="-mx-6 mt-6 w-[calc(100%+48px)] max-w-none object-contain xl:absolute xl:left-[48%] xl:top-[49%] xl:mx-0 xl:mt-0 xl:w-[1340px] xl:-translate-x-1/2 xl:-translate-y-1/2"
                sizes={heroImageSizes}
                src={mobileHeroArtCrop}
                srcSet={`${mobileHeroPngSrcSet}, ${desktopHeroPngSrcSet}`}
              />
            </picture>
          </div>
        </section>

        <div className="relative z-10 mx-auto hidden max-w-[1080px] flex-col items-center justify-center gap-5 px-6 pb-12 text-lg font-medium text-[#56607F] md:-mt-1 md:flex md:flex-row md:gap-10">
          {trustItems.map(([icon, label], index) => (
            <div className={`flex items-center gap-3 ${index > 0 ? 'md:border-l md:border-[#D8DDEA] md:pl-10' : ''}`} key={label}>
              <Mark type={icon} />
              <span>{label}</span>
            </div>
          ))}
        </div>

        <ProblemSection />

        <HowItWorksSection />

        <OrganizerSection />

        <section
          aria-labelledby="product-outcomes-title"
          className="relative overflow-hidden border-b border-[#E1E5F2] bg-white px-6 py-16 text-[#080D3D] md:px-8 md:py-20"
          id="product"
        >
          <div className="mx-auto max-w-[1180px]">
            <div className="mx-auto max-w-[760px] text-center">
              <p className="inline-flex min-h-10 items-center justify-center gap-2 rounded-full bg-[#EDE8FF] px-5 text-[16px] font-black text-[#3926E8]">
                <span aria-hidden="true" className="text-[#FF6B5F]">☼</span>
                Product outcomes
              </p>
              <h2
                className="mt-5 font-serif text-[44px] font-black leading-[0.98] tracking-normal text-[#080D3D] sm:text-[60px] lg:text-[72px]"
                id="product-outcomes-title"
              >
                What CatchUp handles for the <span className="text-[#F5665F]">group</span>
              </h2>
              <p className="mx-auto mt-5 max-w-[650px] text-lg font-medium leading-[1.45] text-[#404B75] sm:text-[20px]">
                The page stays product-led here: less follow-up for organizers, less friction for guests, and clear expectations around messages.
              </p>
            </div>

            <div className="mt-10 grid gap-5 lg:grid-cols-3">
              {productOutcomes.map(({ icon, title, body, detail }) => (
                <article
                  className="group flex min-h-[286px] flex-col rounded-[18px] border border-[#E7E2F6] bg-[linear-gradient(180deg,#FFFFFF_0%,#FBFCFF_100%)] p-6 text-left shadow-[0_24px_64px_rgba(44,32,112,0.08)] transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_30px_78px_rgba(44,32,112,0.12)] motion-reduce:transform-none motion-reduce:transition-none"
                  key={title}
                >
                  <div className="flex items-center justify-between gap-4">
                    <Mark type={icon} />
                    <span className="rounded-full bg-[#F1EDFF] px-3 py-2 text-[13px] font-black text-[#3926E8]">
                      {detail}
                    </span>
                  </div>
                  <h3 className="mt-7 text-[24px] font-black leading-tight text-[#080D3D]">
                    {title}
                  </h3>
                  <p className="mt-4 text-[17px] font-medium leading-[1.5] text-[#404B75]">
                    {body}
                  </p>
                  <div className="mt-auto pt-7">
                    <div className="h-2 rounded-full bg-[#EEE9FF]">
                      <div className="h-2 w-2/3 rounded-full bg-[#3926E8] transition group-hover:w-5/6 motion-reduce:transition-none" />
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <UseCasesSection />

        <SmsTrustSection />

        <FAQSection />

        <FinalCTASection />
      </main>
      <PublicFooter />
    </div>
  );
}

export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white text-slate-950">
      <main className="mx-auto max-w-3xl px-5 py-12">
        <p className="text-sm font-semibold text-[#2C7B8B]">{product}</p>
        <h1 className="mt-2 text-4xl font-bold">Privacy Policy</h1>
        <p className="mt-3 text-sm text-slate-600">Last updated May 3, 2026</p>

        <section className="mt-8 space-y-4 text-base leading-8 text-slate-700">
          <p>
            {product} is operated by {company}, located at {businessAddress}.
            We collect contact information, scheduling preferences, availability
            responses, and message delivery data needed to coordinate events.
          </p>
          <p>
            If you opt in to SMS, we use your phone number to send transactional
            scheduling links, reminders, confirmations, reschedules, and related
            event coordination updates. Message frequency varies. Message and
            data rates may apply.
          </p>
          <p>
            You can Reply STOP to opt out of SMS messages at any time. You can
            Reply HELP for help. We do not sell SMS consent, and we do not share
            SMS consent with third parties for their marketing.
          </p>
          <p>
            We may use service providers, including communications and hosting
            providers, only to operate CatchUp AI and deliver requested messages.
            Contact us at {contactEmail} with privacy questions.
          </p>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}

export function TermsPage() {
  return (
    <div className="min-h-screen bg-white text-slate-950">
      <main className="mx-auto max-w-3xl px-5 py-12">
        <p className="text-sm font-semibold text-[#2C7B8B]">{product}</p>
        <h1 className="mt-2 text-4xl font-bold">Terms of Service</h1>
        <p className="mt-3 text-sm text-slate-600">Last updated May 3, 2026</p>

        <section className="mt-8 space-y-4 text-base leading-8 text-slate-700">
          <p>
            These terms govern your use of {product}, a scheduling coordination
            service operated by {company}, located at {businessAddress}.
          </p>
          <p>
            By providing your phone number and checking the SMS consent box, you
            agree to receive transactional scheduling messages from CatchUp AI.
            These messages may include invitations, availability links,
            confirmations, reschedules, and reminders. Message and data rates
            may apply. Message frequency varies.
          </p>
          <p>
            You may opt out at any time by replying STOP; Reply STOP is the
            supported opt-out instruction. You may get help by replying HELP.
            CatchUp AI messages and links use catchupai.ai.
          </p>
          <p>
            You agree to use CatchUp AI only for lawful coordination and not for
            spam, fraud, prohibited content, or unauthorized messaging. Contact
            us at {contactEmail} with legal questions.
          </p>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}
